import { type ISlotStatusWithMessage } from '../../../store/slices/distributionPlanSlice/apis'

type SetTaskFn = (taskId: string, message: string, successMessage?: string, onComplete?: () => void) => void

interface SlotStatusUpdateDeps {
  setTask: SetTaskFn
  refreshSlotData: () => void
  invalidateSlot: () => void
  openBlockedModal: () => void
}

/**
 * Shared response handler for slot-status update mutations (Publish/Revert/Go Live/Close Project).
 *
 * Sync path  (no task_id) → call `refreshSlotData()` so both the slot header and the section table
 *                            refetch immediately.
 * Async path (task_id)    → register the background task with `setTask`, passing `refreshSlotData`
 *                            as the per-task `onComplete` so the section table refetches when the
 *                            task lands. Also invalidate the slot and open the blocked-by-task modal.
 *
 * Returns `true` if the response was handled (fulfilled), `false` otherwise.
 */
export const handleSlotStatusUpdateResponse = (
  response: { meta?: { requestStatus?: string }, payload?: unknown } | undefined,
  successMessage: string,
  deps: SlotStatusUpdateDeps
): boolean => {
  if (response?.meta?.requestStatus !== 'fulfilled') return false
  const payload = response.payload as ISlotStatusWithMessage | undefined
  if (payload?.task_id != null) {
    deps.setTask(payload.task_id, payload.message, successMessage, deps.refreshSlotData)
    deps.openBlockedModal()
    deps.invalidateSlot()
  } else {
    deps.refreshSlotData()
  }
  return true
}
